import { useState, useCallback } from "react"
import { Form } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { useEngine } from "hooks/api/useEngine"
import { useExportMap } from "features/QueryConsole/api/useExportMap"
import {
  useExportScripts,
  scriptNameFromOption,
  exportScriptFallbackLabel,
  ExportScript,
  ExportScriptOption,
} from "features/QueryConsole/api/useExportScripts"
import { useRecordDeliverable, DeliverableDetails } from "features/Provenance"
import { downloadManifestJson, downloadResponseAsFile } from "utils/files/downloadUtils"
import { QueryInstance } from "types/engine.types"
import {
  SPSSFormValues,
  CSVFormValues,
  EXPORT_VARIABLE_TYPES,
  EXPORT_FORMAT,
  SPS_EXPORT_TYPE,
  DATA_FORMAT,
  CSV_CAT_OPTION,
  JSONL_FLAT_SEPARATOR,
} from "types/engineExport.types"

export interface InputValues {
  filename: string
  variableType: EXPORT_VARIABLE_TYPES | ""
  dataFormat: DATA_FORMAT
  // A normal categorical format, or a unit's export script (script:<name>)
  csvCatOption: CSV_CAT_OPTION | ExportScriptOption
  jsonlUseLabels: boolean
  jsonlFlatSeparator: JSONL_FLAT_SEPARATOR
  spsOutputFormat: "sps" | "sav"
  csvVariableType: EXPORT_VARIABLE_TYPES | ""
}

interface FilenameFormValues {
  filename: string
}

/** The dialog is opened from outside (the console's Tools menu), so whether
 *  it is open comes in as `open` and `onOpenChange`. */
export const useExportDataModal = (
  queryInstance: QueryInstance | null,
  open: boolean,
  onOpenChange: (open: boolean) => void
) => {
  const [inputValues, setInputValues] = useState<InputValues>({
    filename: "",
    variableType: "",
    dataFormat: DATA_FORMAT.CATEGORIZED,
    csvCatOption: CSV_CAT_OPTION.NORMAL,
    jsonlUseLabels: false,
    jsonlFlatSeparator: JSONL_FLAT_SEPARATOR.NONE,
    spsOutputFormat: "sps",
    csvVariableType: "",
  })
  const isModalVisible = open
  const setIsModalVisible = onOpenChange
  // The export record box: also download the record the server builds for the export
  const [includeExportRecord, setIncludeExportRecord] = useState(false)
  const [isExportMapModalOpen, setIsExportMapModalOpen] = useState(false)
  const [exportMapFormat, setExportMapFormat] = useState<"sps" | "sav" | "csv">("sps")

  const {
    exportJSON, isExportingJSON,
    exportJSONL, isExportingJSONL,
    exportSPS, isExportingSPS,
    exportSAV, isExportingSAV,
    exportDQD, isExportingDQD,
  } = useEngine()
  const { exportWithCustomMap, exportCsvDirect, isExportingCsvDirect } = useExportMap()
  const { exportAndRecord } = useRecordDeliverable()
  const { exportScripts, runScriptExport } = useExportScripts(open)

  // The export script chosen as the CSV's categorical format, or null for a normal format
  const selectedExportScript = ((): ExportScript | null => {
    const name = scriptNameFromOption(inputValues.csvCatOption)
    if (!name) return null
    return exportScripts.find((s) => s.name === name) ?? { name, label: exportScriptFallbackLabel(name) }
  })()

  const [spssForm] = Form.useForm<SPSSFormValues>()
  const [csvForm] = Form.useForm<CSVFormValues>()
  const [filenameForm] = Form.useForm<FilenameFormValues>()

  const handleInputChange = <K extends keyof InputValues>(
    key: K,
    value: InputValues[K]
  ): void => {
    setInputValues((prev) => ({ ...prev, [key]: value }))
  }

  const validateFilename = useCallback(async (): Promise<boolean> => {
    try {
      await filenameForm.validateFields()
      return true
    } catch {
      return false
    }
  }, [filenameForm])

  /**
   * Runs an export (resolving to the file it wrote, or null when it failed) and records it in
   * the project history: every successful export is recorded. With the export record box
   * ticked, the record the server built is downloaded next to the file. Resolves to whether
   * the file was exported.
   */
  const recordedExport = async (
    details: Omit<DeliverableDetails, "filename" | "instanceId">,
    run: () => Promise<File | null>
  ): Promise<boolean> => {
    // The name the exported file is saved under, without its extension
    const filename = inputValues.filename || "export"
    const { exported, recorded } = await exportAndRecord(
      { ...details, filename, instanceId: queryInstance ?? undefined },
      run
    )
    if (recorded && includeExportRecord) downloadManifestJson(recorded.certificate, filename)
    return exported
  }

  /** The file an export wrote, or a thrown error when it wrote none (the handler reports it). */
  const written = (file: File | null): File => {
    if (!file) throw new Error("The file could not be saved.")
    return file
  }

  const handleJSONExport = async (): Promise<boolean> => {
    if (!queryInstance) return false
    const isValid = await validateFilename()
    if (!isValid) return false
    return recordedExport({ format: "json" }, async () => {
      try {
        const file = written(
          await exportJSON(queryInstance, inputValues.filename, inputValues.dataFormat)
        )
        notify.toast.success("JSON export completed successfully")
        return file
      } catch (error) {
        console.error("JSON export failed:", error)
        notify.toast.error("JSON export failed")
        return null
      }
    })
  }

  const handleJSONLExport = async (): Promise<boolean> => {
    if (!queryInstance) return false
    const isValid = await validateFilename()
    if (!isValid) return false
    return recordedExport({ format: "jsonl" }, async () => {
      try {
        const file = written(
          await exportJSONL(queryInstance, inputValues.filename, inputValues.jsonlUseLabels, inputValues.jsonlFlatSeparator)
        )
        notify.toast.success("JSONL export completed successfully")
        return file
      } catch (error) {
        console.error("JSONL export failed:", error)
        notify.toast.error("JSONL export failed")
        return null
      }
    })
  }

  const handleSPSSExport = async (): Promise<boolean> => {
    if (!queryInstance) return false
    const isValid = await validateFilename()
    if (!isValid) return false
    try {
      await spssForm.validateFields()
    } catch (error) {
      console.error("SPSS export failed:", error)
      notify.toast.error("SPSS export failed")
      return false
    }
    return recordedExport({ format: "spss-sps" }, async () => {
      try {
        const file = written(
          await exportSPS(
            queryInstance,
            inputValues.variableType as EXPORT_VARIABLE_TYPES,
            inputValues.filename
          )
        )
        notify.toast.success("SPSS export completed successfully")
        return file
      } catch (error) {
        console.error("SPSS export failed:", error)
        notify.toast.error("SPSS export failed")
        return null
      }
    })
  }

  const handleSAVExport = async (): Promise<boolean> => {
    if (!queryInstance) return false
    const isValid = await validateFilename()
    if (!isValid) return false
    try {
      await spssForm.validateFields()
    } catch (error) {
      console.error("SAV export failed:", error)
      notify.toast.error("SAV export failed")
      return false
    }
    return recordedExport({ format: "spss-sav" }, async () => {
      try {
        const file = written(
          await exportSAV(
            queryInstance,
            inputValues.variableType as EXPORT_VARIABLE_TYPES,
            inputValues.filename
          )
        )
        notify.toast.success("SAV export completed successfully")
        return file
      } catch (error) {
        console.error("SAV export failed:", error)
        notify.toast.error("SAV export failed")
        return null
      }
    })
  }

  const handleCSVExport = async (): Promise<boolean> => {
    if (!queryInstance) return false
    // A client format needs the export map (the panel disables this export for one)
    if (selectedExportScript) return false
    const isValid = await validateFilename()
    if (!isValid) return false
    try {
      await csvForm.validateFields()
    } catch (error) {
      console.error("CSV export failed:", error)
      notify.toast.error("CSV export failed")
      return false
    }
    const varType =
      inputValues.csvVariableType === EXPORT_VARIABLE_TYPES.NUMBERING
        ? EXPORT_VARIABLE_TYPES.NUMBERING
        : EXPORT_VARIABLE_TYPES.LABELS
    return recordedExport({ format: "csv" }, async () => {
      try {
        const response = await exportCsvDirect(queryInstance, varType, inputValues.csvCatOption as CSV_CAT_OPTION, inputValues.filename || "export")
        const file = written(downloadResponseAsFile(response, `${inputValues.filename || "export"}.zip`))
        notify.toast.success("CSV export completed successfully")
        return file
      } catch (error) {
        console.error("CSV export failed:", error)
        notify.toast.error("CSV export failed")
        return null
      }
    })
  }

  const handleDQDExport = async (): Promise<boolean> => {
    if (!queryInstance) return false
    const isValid = await validateFilename()
    if (!isValid) return false
    return recordedExport({ format: "dqd" }, async () => {
      try {
        const file = written(
          await exportDQD(queryInstance, inputValues.filename, inputValues.dataFormat)
        )
        notify.toast.success("DQD export completed successfully")
        return file
      } catch (error) {
        console.error("DQD export failed:", error)
        notify.toast.error("DQD export failed")
        return null
      }
    })
  }

  const handleOpenExportMap = useCallback(
    async (format: "sps" | "sav" | "csv") => {
      const isValid = await validateFilename()
      if (!isValid) {
        notify.toast.error("Please enter a filename first")
        return
      }
      try {
        if (format === "csv") {
          await csvForm.validateFields()
        } else {
          await spssForm.validateFields()
        }
        setExportMapFormat(format)
        setIsExportMapModalOpen(true)
      } catch {
        notify.toast.error("Please select a variable type first")
      }
    },
    [validateFilename, spssForm, csvForm]
  )

  /**
   * Exports with an edited export map and records the export with the map's text (the server
   * hashes it). Throws when the export fails, so the map dialog stays open.
   */
  const handleExportWithMap = async (exportMapCsv: string): Promise<void> => {
    if (!queryInstance) return
    if (exportMapFormat === "csv" && selectedExportScript) {
      return handleExportScriptWithMap(exportMapCsv, selectedExportScript)
    }
    const filename = inputValues.filename || "export"
    const formatMap = {
      sps: EXPORT_FORMAT.SPS,
      sav: EXPORT_FORMAT.SAV,
      csv: EXPORT_FORMAT.CSV,
    }

    const varType = exportMapFormat === "csv"
      ? (inputValues.csvVariableType === EXPORT_VARIABLE_TYPES.NUMBERING
          ? EXPORT_VARIABLE_TYPES.NUMBERING
          : EXPORT_VARIABLE_TYPES.LABELS)
      : (inputValues.variableType === EXPORT_VARIABLE_TYPES.NUMBERING
          ? EXPORT_VARIABLE_TYPES.NUMBERING
          : EXPORT_VARIABLE_TYPES.LABELS)

    let failure: unknown = null
    const exported = await recordedExport(
      { format: exportMapFormat || "mapped", exportMap: exportMapCsv },
      async () => {
        try {
          const response = await exportWithCustomMap(queryInstance, exportMapCsv, {
            format: formatMap[exportMapFormat],
            varType,
            spsType: SPS_EXPORT_TYPE.SPS_DAT,
            filename,
            catOption: inputValues.csvCatOption as CSV_CAT_OPTION,
          })
          const file = written(downloadResponseAsFile(response, `${filename}.zip`))
          const formatLabel = exportMapFormat === "sps" ? "SPSS" : exportMapFormat === "sav" ? "SAV" : "CSV"
          notify.toast.success(`${formatLabel} export completed successfully`)
          setIsModalVisible(false)
          return file
        } catch (error) {
          failure = error
          return null
        }
      }
    )
    if (!exported) throw failure ?? new Error("The export failed.")
  }

  /**
   * A client format: the normal CSV export with the map goes through the unit's export script,
   * and what the script wrote is downloaded and recorded. Throws when it fails, as above.
   */
  const handleExportScriptWithMap = async (
    exportMapCsv: string,
    script: ExportScript
  ): Promise<void> => {
    if (!queryInstance) return
    const filename = inputValues.filename || "export"

    let failure: unknown = null
    const exported = await recordedExport(
      { format: `script:${script.name}`, exportMap: exportMapCsv },
      async () => {
        try {
          // The script works on the normal layout (category codes + map) with the question
          // labels in the map, so always Labelled
          const response = await exportWithCustomMap(queryInstance, exportMapCsv, {
            format: EXPORT_FORMAT.CSV,
            varType: EXPORT_VARIABLE_TYPES.LABELS,
            spsType: SPS_EXPORT_TYPE.SPS_DAT,
            filename,
            catOption: CSV_CAT_OPTION.NORMAL,
          })
          const file = await runScriptExport(script, response.data, filename)
          setIsModalVisible(false)
          return file
        } catch (error) {
          failure = error
          return null
        }
      }
    )
    if (!exported) throw failure ?? new Error("The export failed.")
  }

  return {
    inputValues,
    handleInputChange,
    isModalVisible,
    setIsModalVisible,
    includeExportRecord,
    setIncludeExportRecord,
    isExportMapModalOpen,
    setIsExportMapModalOpen,
    exportMapFormat,
    exportScripts,
    selectedExportScript,
    filenameForm,
    spssForm,
    csvForm,
    // Export handlers
    handleJSONExport,
    handleJSONLExport,
    handleSPSSExport,
    handleSAVExport,
    handleCSVExport,
    handleDQDExport,
    handleOpenExportMap,
    handleExportWithMap,
    // Loading states
    isExportingJSON,
    isExportingJSONL,
    isExportingSPS,
    isExportingSAV,
    isExportingDQD,
    isExportingCsvDirect,
  }
}
